import { useId, useState, type ChangeEvent, type InputHTMLAttributes } from 'react'
import { US_PHONE_PLACEHOLDER, caretAfterDigits, formatUsPhone, hasUsCountryCode, isCompleteUsPhone, isInternationalPhone } from './phoneFormat.js'

type PhoneInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'inputMode'> & {
  value: string
  /** Called with the number as shown, "(555) 555-0123"; the API reads that spelling. */
  onChange: (value: string) => void
}

/** A whole US number as formatted, or an international one: the browser will not submit a form with a number half typed. */
const COMPLETE_PHONE_PATTERN = String.raw`\(\d{3}\) \d{3}-\d{4}|\+[2-9][\d \(\)\.\-]{6,30}`

/** How many of the number's own digits sit before `caret` in `raw`, the country code 1 not counted. */
function digitsBeforeCaret(raw: string, caret: number): number {
  const count = raw.slice(0, caret).replace(/\D/g, '').length
  return hasUsCountryCode(raw) ? Math.max(0, count - 1) : count
}

/** The text with the digit just before `caret` removed, for a backspace that landed on a bracket, space or dash. */
function withoutDigitBefore(raw: string, caret: number): { raw: string; caret: number } {
  for (let index = caret - 1; index >= 0; index -= 1) {
    if (/\d/.test(raw[index] ?? '')) {
      return { raw: raw.slice(0, index) + raw.slice(index + 1), caret: index }
    }
  }
  return { raw, caret }
}

/**
 * A US phone field that formats as the person types: "(555) 555-0123",
 * the brackets, space and dash appearing as the digits arrive, the caret
 * kept beside the digit it was at, and a backspace over a bracket or dash
 * removing the digit before it. Pasting "+1 555.555.0123" or "5555550123"
 * gives the same result. The number pad opens on a phone (`inputMode`),
 * and a number left part-typed is marked, with a line saying so once the
 * person leaves the field. A number starting with a country code other
 * than +1 is left as typed.
 */
export function PhoneInput({ value, onChange, placeholder = US_PHONE_PLACEHOLDER, autoComplete = 'tel-national', onBlur, className = '', ...inputProps }: PhoneInputProps) {
  const [touched, setTouched] = useState(false)
  const hintId = useId()
  const shown = formatUsPhone(value)
  const incomplete = touched && !isCompleteUsPhone(shown)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let raw = event.target.value
    let caret = event.target.selectionStart ?? raw.length
    const deletingBack = (event.nativeEvent as InputEvent).inputType === 'deleteContentBackward'
    if (deletingBack && raw.replace(/\D/g, '') === shown.replace(/\D/g, '') && !isInternationalPhone(raw)) {
      ;({ raw, caret } = withoutDigitBefore(raw, caret))
    }
    const next = formatUsPhone(raw)
    const nextCaret = isInternationalPhone(raw) ? caret : caretAfterDigits(next, digitsBeforeCaret(raw, caret))
    // Written to the field here, so the caret holds even when the number does not change (an eleventh digit).
    event.target.value = next
    event.target.setSelectionRange(nextCaret, nextCaret)
    onChange(next)
  }

  return (
    <span className="flex min-w-0 flex-1 flex-col">
      <input
        {...inputProps}
        type="tel"
        inputMode="tel"
        autoComplete={autoComplete}
        placeholder={placeholder}
        pattern={COMPLETE_PHONE_PATTERN}
        title={`A 10-digit US number, like ${US_PHONE_PLACEHOLDER}, or an international number starting with +`}
        value={shown}
        onChange={handleChange}
        onBlur={(event) => {
          setTouched(true)
          onBlur?.(event)
        }}
        aria-invalid={incomplete || undefined}
        aria-describedby={incomplete ? hintId : inputProps['aria-describedby']}
        className={`${className}${incomplete ? ' border-coral' : ''}`}
      />
      {incomplete && (
        <span id={hintId} className="mt-1 text-[11px] text-coral">
          Enter all 10 digits, like {US_PHONE_PLACEHOLDER}.
        </span>
      )}
    </span>
  )
}
